<template>
  <Modal :style="computedWindowSize"
         :visible="visible"
         class="p-fluid"
         @close="close">
    <template v-slot:header>
      <div class="p-dialog-title">{{ item?.id ? $t('tenantCompanyEditModalTitle') : $t('tenantCompanyCreateModalTitle') }}</div>
    </template>

    <Spinner v-show="dataIsSending"/>

    <div v-show="!dataIsSending" class="p-formgrid p-grid p-pt-3">
      <div class="p-field p-col-12 p-sm-12 p-md-12 p-lg-12 p-mb-5" v-if="$store.getters.isLocationInEstonia">
        <EECompanyRegisterDropdown v-model="selectedCompany"
                                   :disabled="dataIsSending"
                                   :resetOnClose="true"
                                   :visible="visible"
                                   :showClear="true"
                                   @change="changeCompany"
                                   inputId="company-modal-ee-company-dropdown"/>
      </div>

      <div class="p-field p-col-12 p-sm-12 p-md-12 p-lg-12 p-mb-5">
        <div class="p-float-label">
          <InputText id="company-modal-name"
                     :class="{'p-invalid' : submitted && !itemData.name}"
                     v-model.trim="itemData.name"
                     autocomplete="off"/>
          <label for="company-modal-name">{{$t('Name')}}<span class="warning-color">*</span></label>
        </div>
        <small class="p-invalid" v-if="submitted && !itemData.name">{{ $t('Required field') }}</small>
      </div>

      <div class="p-field p-col-12 p-sm-6 p-md-6 p-lg-6 p-mb-5">
        <div class="p-float-label">
          <InputText id="company-modal-reg-number"
                     :class="{'p-invalid' : submitted && !itemData.reg_number}"
                     v-model.trim="itemData.reg_number"
                     autocomplete="off"/>
          <label for="company-modal-reg-number">{{$t('Reg. number')}}<span class="warning-color">*</span></label>
        </div>
        <small class="p-invalid" v-if="submitted && !itemData.reg_number">{{ $t('Required field') }}</small>
      </div>

      <div class="p-field p-col-12 p-sm-6 p-md-6 p-lg-6 p-mb-5">
        <div class="p-float-label">
          <InputText id="company-modal-vat-number" v-model.trim="itemData.vat_number" autocomplete="off"/>
          <label for="company-modal-vat-number">{{$t('VAT number')}}</label>
        </div>
      </div>

      <div class="p-field p-col-12 p-sm-6 p-md-6 p-lg-6 p-mb-5">
        <div class="p-float-label">
          <CustomDropdown id="company-default-tax"
                          data-key="id"
                          :class="{'p-invalid' : submitted && !selectedTax}"
                          v-model="selectedTax"
                          :clearSearchData="!visible"
                          :loading="taxesInFetch"
                          :options="taxesData"
                          :filter="true"
                          :filterFields="['name', 'value']"
                          :showClear="false">
            <template #value="slotProps">
              <div v-if="slotProps.value">
                <span>{{ formatDecimal(slotProps.value.value) }}% <span v-if="slotProps.value.name">({{ slotProps.value.name }})</span></span>
              </div>
              <span v-else style="visibility: hidden">.</span>
            </template>
            <template #option="slotProps">
              <div>
                <span>{{ formatDecimal(slotProps.option.value) }}% <span v-if="slotProps.option.name">({{ slotProps.option.name }})</span></span>
              </div>
            </template>
          </CustomDropdown>
          <label for="company-default-tax">{{ $t('defaultVatRateLabel') }}<span class="warning-color">*</span></label>
        </div>
        <small class="p-invalid" v-if="submitted && !selectedTax">{{ $t('Required field') }}</small>
      </div>

      <div class="p-field p-col-12 p-sm-6 p-md-6 p-lg-6 p-mb-5">
        <div class="p-float-label">
          <InputNumber :locale="computedNumberInputLocale"
                       v-model="itemData.late_fee_percent"
                       id="company-modal-late-fee-percent"
                       mode="decimal"
                       suffix="%"
                       :min="0"
                       :max="100"
                       :minFractionDigits="2"
                       :maxFractionDigits="2"
                       :showButtons="false"
                       autocomplete="off"/>
          <label for="company-modal-late-fee-percent">{{ $t('penaltyPercentLabel') }}</label>
        </div>
      </div>

      <div class="p-field p-col-12 p-sm-12 p-md-12 p-lg-12 p-mb-5">
        <!--                    label="Privacy Email"-->
        <EmailInput v-model="itemData.privacy_email"
                    v-model:isInvalid="privacyEmailInvalid"
                    :submitted="submitted"
                    label="companyEmailLabel"
                    :required="true"
                    inputId="company-modal-privacy-email"/>
      </div>

      <div class="p-field p-col-12 p-sm-12 p-md-12 p-lg-12 p-mb-5">
        <EmailInput v-model="itemData.billing_email"
                    v-model:isInvalid="billingEmailInvalid"
                    :submitted="submitted"
                    label="Billing Email"
                    :required="true"
                    inputId="company-modal-billing-email"/>
      </div>

      <div class="p-field p-col-12 p-sm-12 p-md-12 p-lg-12 p-mb-5">
        <!--        label="Privacy Phone"-->
        <PhoneInput v-model:phoneCountry="itemData.privacy_phone_country"
                    v-model:phoneCode="itemData.privacy_phone_code"
                    v-model:phoneNumber="itemData.privacy_phone_number"
                    v-model:isInvalid="privacyPhoneInvalid"
                    :name="'company-modal-privacy'"
                    label="companyPhoneLabel"
                    :required="false"
                    :modalData="itemData"
                    :visible="visible"
                    :submitted="submitted"/>
      </div>


      <div class="p-field p-col-12 p-sm-12 p-md-12 p-lg-12">
        <AccountAddressFieldset v-model="itemData"
                                label="companyAddressLabel"
                                :invalid="submitted && addressIsInvalid"
                                :modalLayer="1"/>
      </div>
    </div>
    <template #footer>
      <Button :label="$t('Cancel')" icon="pi pi-times" class="p-button-text" @click="close"/>
      <Button :label="$t('Save')" :disabled="dataIsSending" icon="pi pi-check" class="p-button-text" @click="saveItem"/>
    </template>
  </Modal>
</template>

<script>
import httpClient from '@/services/http.services'
import formatMixins from "@/mixins/formatMixins";
import settings from "@/settings";
import showErrorsMixins from "@/mixins/showErrorsMixins";
import EmailInput from "@/components/EmailInput";
import PhoneInput from "@/components/PhoneInput";
import httpMixins from "@/mixins/httpMixins";
import EECompanyRegisterDropdown from "@/components/EECompanyRegisterDropdown.vue";
import AccountAddressFieldset from "@/pages/account/components/AccountAddressFieldset.vue";

export default {
  components: { EECompanyRegisterDropdown, EmailInput, PhoneInput, AccountAddressFieldset },
  mixins: [ showErrorsMixins, formatMixins, httpMixins ],
  emits: ['close', 'update-items'],
  name: 'TenantCompanyModal',
  props: {
    item: Object,
    visible: Boolean,
  },
  data() {
    return {
      selectedCompany: null,
      privacyEmailInvalid: false,
      billingEmailInvalid: false,
      privacyPhoneInvalid: false,
      dataIsSending: false,
      itemData: {},
      submitted: false,
    }
  },
  watch: {
    item: {
      immediate: true,
      handler(value) {
        this.setItemData(value)
      },
    },

    visible(value) {
      if (!value) {
        this.dataIsSending = false
        this.submitted = false
        this.taxesInFetch = false
        this.selectedTax = null
        this.privacyEmailInvalid = false
        this.billingEmailInvalid = false
        this.privacyPhoneInvalid = false
        this.itemData = {}
        this.selectedCompany = null
        return
      }

      this.setItemData(this.item)
      this.getTaxes().then(() => this.setDefaultTax())
    },
  },
  methods: {
    setItemData(item) {
      this.itemData = item
          ? {
            ...item,
            late_fee_percent:
                item.late_fee_percent != null
                    ? +item.late_fee_percent
                    : 0,
            vat_number: item.tax_number,
          }
          : {}

      this.selectedTax = item?.default_tax ?? null
    },
    setDefaultTax() {
      if (this.itemData?.default_tax) {
        this.selectedTax = this.itemData.default_tax
      } else if (this.itemData?.default_tax_id) {
        this.selectedTax = this.taxesData?.find(t => +t.id === +this.itemData.default_tax_id)
      }
    },
    changeCompany() {
      const company = this.selectedCompany

      if (!company) {
        this.itemData = {
          ...this.itemData,
          name: null,
          reg_number: null,
          vat_number: null,
          address1: null,
          address2: null,
          city: null,
          region: null,
          postcode: null,
          country_id: null,
          country: null,
          country_name: null,
        }
        return
      }

      this.itemData = {
        ...this.itemData,

        name: company.name ?? null,
        reg_number: company.registry_code ?? null,
        vat_number: company.vat ?? null,

        address1: company.address ?? null,
        address2: company.address2 ?? null,
        city: company.city ?? null,
        region: company.state ?? null,
        postcode: company.postcode ?? null,

        country_id: company.country_id ? Number(company.country_id) : null,
        country: company.country ?? null,
        country_name: company.country?.name_en ?? company.country_name ?? null,
      }
    },
    async saveItem() {
      this.submitted = true

      if (
          !this.itemData
          || !this.itemData.name
          || !this.itemData.reg_number
          || !this.selectedTax
          || !this.itemData.privacy_email
          || this.privacyEmailInvalid
          || !this.itemData.billing_email
          || this.billingEmailInvalid
          || this.privacyPhoneInvalid
          || this.addressIsInvalid
      ) {
        return false
      }

      this.dataIsSending = true

      const savePhoneNumber = this.itemData.privacy_phone_code && this.itemData.privacy_phone_number

      const companyData = {
        id: this.itemData.id ?? null,
        name: this.itemData.name ?? null,
        reg_number: this.itemData.reg_number ?? null,
        tax_number: this.itemData.vat_number ?? null,
        late_fee_percent: this.itemData.late_fee_percent ?? null,
        default_tax_id: this.selectedTax?.id ? +this.selectedTax.id : null,
        privacy_email: this.itemData.privacy_email ?? null,
        billing_email: this.itemData.billing_email ?? null,
        privacy_phone_country: savePhoneNumber && this.itemData.privacy_phone_country ? this.itemData.privacy_phone_country : null,
        privacy_phone_code: savePhoneNumber ? this.itemData.privacy_phone_code : null,
        privacy_phone_number: savePhoneNumber ? this.itemData.privacy_phone_number : null,
        address1: this.itemData.address1 ?? null,
        address2: this.itemData.address2 ?? null,
        city: this.itemData.city ?? null,
        region: this.itemData.region ?? null,
        postcode: this.itemData.postcode ?? null,
        country_id: this.itemData.country_id ?? null,
      }

      if (this.itemData.id) {
        try {
          const { status } = await httpClient.post(`tenant-company/update`, companyData)
          if (status === 200) {
            this.$store.commit('updateLocationDataVariable', { variableName: 'default_tax_id', value: this.selectedTax.id})
            this.$emit('update-items', this.itemData.id)
            this.$toast.add({severity: 'success', detail: this.$t('Data updated'), life: settings.toastLife});
            this.close()
          } else {
            this.showUnknownError()
          }
        } catch(err) {
          this.showError(err)
        } finally {
          this.dataIsSending = false
        }
      } else {
        try {
          const { status, data } = await httpClient.post(`tenant-company/create`, companyData)
          if (status === 201 && data) {
            this.itemData.id = data.id
            companyData.id = data.id
            this.$emit('update-items', this.itemData.id)
            this.$toast.add({severity:'success', detail: this.$t('Data saved'), life: settings.toastLife});
            this.close()
          } else {
            this.showUnknownError()
          }
        } catch(err) {
          this.showError(err)
        } finally {
          this.dataIsSending = false
        }
      }
    },
    close() {
      this.$emit('close')
      this.submitted = false
    },
  },
  computed: {
    addressIsInvalid() {
      return !(
          this.itemData?.address1
          && this.itemData?.city
          && this.itemData?.postcode
          && this.itemData?.country_id
      )
    },

    computedWindowSize() {
      if (this.$store.state.mobileLayout && this.$store.state.tabletLayout) {
        return {position: 'absolute', top: 0, width: '95%', maxWidth: '440px', marginTop: '10px'}
      } else if (!this.$store.state.mobileLayout && this.$store.state.tabletLayout) {
        return {position: 'absolute', top: 0, width: '440px', marginTop: '20px'}
      } else {
        return {position: 'absolute', top: 0, width: '440px', marginTop: '20px'}
      }
    },
  },

}
</script>